if (!customElements.get('sticky-atc-bar-minimalist')) {
  customElements.define(
    'sticky-atc-bar-minimalist',
    class StickyAtcBarMinimalist extends HTMLElement {
      constructor() {
        super();

        this.classes = {
          isOpen: 'sticky-atc-bar--show',
          isExpanded: 'is-expanded'
        };
      }

      get submitEls() {
        return this.querySelectorAll('button[name="add"], .sticky-atc-bar__mobile-submit');
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');
        this.targetObserver = document.querySelector('.product__block--buy_buttons') || this.productFormActions;

        this.submitEls.forEach(btn => {
          btn.addEventListener('click', this.onClick.bind(this));
        });

        this.init();

        this.cartErrorUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, () => {
          this.resetButtonLoading();
          this.scrollToTop(this.productFormActions);
        });

        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
          this.resetButtonLoading();
          // Reset only the sticky bar's quantity inputs to 1
          this.querySelectorAll('input[name="quantity"]').forEach(input => {
            input.value = 1;
            input.dispatchEvent(new CustomEvent('change', { detail: { preventScroll: true }, bubbles: true }));
          });
        });
      }

      initMobileImageResize() {
        this.mobileInfo = this.querySelector('.sticky-atc-bar__mobile-info');
        this.mobileImageWrapper = this.querySelector('.sticky-atc-bar__mobile-product-details .sticky-atc-bar__product-image');
        this.mobileImage = this.mobileImageWrapper?.querySelector('img');
        this.mobileContainer = this.querySelector('.sticky-atc-bar__mobile-product-details');

        if (!this.mobileInfo || !this.mobileImage || !this.mobileContainer) return;

        this.mobileResizeObserver = new ResizeObserver(() => {
          this.updateMobileImageSize();
        });

        this.mobileResizeObserver.observe(this.mobileInfo);
        this.mobileResizeObserver.observe(this.mobileContainer);
      }

      updateMobileImageSize() {
        if (!this.classList.contains(this.classes.isExpanded)) return;

        const infoHeight = this.mobileInfo.offsetHeight;
        const containerWidth = this.mobileContainer.offsetWidth;
        const gap = 16;
        const maxAvailableWidth = (containerWidth - gap) / 2;

        const size = Math.min(infoHeight, maxAvailableWidth);

        if (size > 0) {
          this.mobileImage.style.width = `${size}px`;
          this.mobileImage.style.height = `${size}px`;
        }
      }

      disconnectedCallback() {
        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.mobileResizeObserver) {
          this.mobileResizeObserver.disconnect();
        }
      }

      init() {
        if (!this.targetObserver) {
          this.classList.remove(this.classes.isOpen);
          return;
        }

        const rootMargin = `${this.targetObserver.offsetHeight}px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const isNotVisible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.classList.toggle(this.classes.isOpen, isNotVisible);
            });
          },
          { threshold: 0, rootMargin: '0px' }
        );
        this.observer.observe(this.targetObserver);
        this.syncWithMainProductForm();
        this.bindVariantSelectors();
        this.bindCustomSelects();
        this.bindMobileToggle();
        this.initMobileImageResize();
        this.bindQuantitySync();
      }

      bindCustomSelects() {
        // Handle Desktop Custom Dropdowns
        const customSelects = this.querySelectorAll('.sticky-atc-bar__custom-select');

        customSelects.forEach(select => {
          const trigger = select.querySelector('.sticky-atc-bar__custom-select-trigger').closest('button');
          const dropdown = select.querySelector('.sticky-atc-bar__custom-select-dropdown');
          const input = select.querySelector('.sticky-atc-bar__custom-select-input');
          const label = select.querySelector('.sticky-atc-bar__custom-select-label');
          const triggerSwatch = select.querySelector('.sticky-atc-bar__custom-select-trigger .swatch-color');
          const items = select.querySelectorAll('.sticky-atc-bar__custom-select-item');

          // Toggle dropdown
          trigger.addEventListener('click', (e) => {
            e.stopPropagation();
            const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

            // Close all others
            this.querySelectorAll('.sticky-atc-bar__custom-select button[aria-expanded="true"]').forEach(btn => {
              btn.setAttribute('aria-expanded', 'false');
              btn.nextElementSibling.nextElementSibling.classList.add('hidden'); // The UL
            });

            if (!isExpanded) {
              trigger.setAttribute('aria-expanded', 'true');
              dropdown.classList.remove('hidden');
            }
          });

          // Handle Selection
          items.forEach(item => {
            item.addEventListener('click', (e) => {
              e.stopPropagation();

              // Remove active state from siblings
              items.forEach(sib => sib.classList.remove('is-selected'));
              item.classList.add('is-selected');

              const newValue = item.dataset.value;
              input.value = newValue;
              label.textContent = newValue;

              const swatchBg = item.dataset.swatch;
              if (triggerSwatch) {
                if (swatchBg) {
                  triggerSwatch.style.cssText = `--swatch-background: ${swatchBg}; width: 20px; height: 20px; box-shadow: inset 0 0 0 1px rgba(0,0,0,0.1), 0 0 0 2px rgb(var(--color-background, 255, 255, 255)), 0 0 0 3px rgb(var(--color-foreground, 0, 0, 0));`;
                  triggerSwatch.classList.remove('hidden');
                } else {
                  triggerSwatch.classList.add('hidden');
                }
              }

              // Close dropdown
              trigger.setAttribute('aria-expanded', 'false');
              dropdown.classList.add('hidden');

              // Sync to form logic
              this.syncOptionToMainForm(input.dataset.optionName, newValue);

              // Update mobile option value label if it exists (for mobile expanded view)
              const mobileOptionWrapper = select.closest('.sticky-atc-bar__mobile-option');
              if (mobileOptionWrapper) {
                const valLabel = mobileOptionWrapper.querySelector('.sticky-atc-bar__mobile-val');
                if (valLabel) valLabel.textContent = newValue;
              }
            });
          });
        });

        // Click outside to close
        document.addEventListener('click', (e) => {
          if (!e.target.closest('.sticky-atc-bar__custom-select')) {
            this.querySelectorAll('.sticky-atc-bar__custom-select button[aria-expanded="true"]').forEach(btn => {
              btn.setAttribute('aria-expanded', 'false');
              btn.nextElementSibling.nextElementSibling.classList.add('hidden');
            });
          }
        });
      }

      bindMobileToggle() {
        const toggleBtn = this.querySelector('.sticky-atc-bar__mobile-toggle');
        const closeBtn = this.querySelector('.sticky-atc-bar__mobile-close');

        if (toggleBtn && closeBtn) {
          const toggleExpand = () => {
            this.classList.toggle(this.classes.isExpanded);
            if (this.classList.contains(this.classes.isExpanded)) {
              setTimeout(() => this.updateMobileImageSize(), 0);
            }
          }
          toggleBtn.addEventListener('click', toggleExpand);
          closeBtn.addEventListener('click', toggleExpand);
        }
      }

      syncOptionToMainForm(optionName, value) {
        if (this.productFormActions) {
          const variantSelects = document.querySelector('variant-selects');
          if (variantSelects) {
            // Find by value because input names are dynamic (e.g. Color-1)
            // Use CSS escape to handle values with quotes or special characters
            const escapedValue = CSS.escape(value);
            const matchingOption = variantSelects.querySelector(`input[type="radio"][value="${escapedValue}"], select option[value="${escapedValue}"]`) ||
              variantSelects.querySelector(`input[type="radio"][value="${value}"], select option[value="${value}"]`);

            if (matchingOption) {
              if (matchingOption.tagName === 'OPTION') {
                const parentSelect = matchingOption.closest('select');
                if (parentSelect && parentSelect.value !== value) {
                  parentSelect.value = value;
                  parentSelect.dispatchEvent(new CustomEvent('change', { detail: { preventScroll: true }, bubbles: true }));
                }
              } else if (matchingOption.tagName === 'INPUT' && !matchingOption.checked) {
                matchingOption.checked = true;
                matchingOption.dispatchEvent(new CustomEvent('change', { detail: { preventScroll: true }, bubbles: true }));
              }
            }
          }
        }
      }

      bindQuantitySync() {
        const syncAll = (val, origin) => {
          const allInputs = document.querySelectorAll('input[name="quantity"]');
          allInputs.forEach(input => {
            if (input !== origin && input.value !== val) {
              input.value = val;
              // Dispatch change event to trigger other listeners
              input.dispatchEvent(new CustomEvent('change', { detail: { preventScroll: true }, bubbles: true }));

              // Handle quantity-input custom element if present
              const quantityInput = input.closest('quantity-input');
              if (quantityInput && typeof quantityInput.onInputChange === 'function') {
                // Some themes might need this
              }
            }
          });
        };

        // Use event delegation for robustness against dynamic DOM updates
        document.addEventListener('change', (e) => {
          if (e.target.matches('input[name="quantity"]')) {
            syncAll(e.target.value, e.target);
          }
        });

        document.addEventListener('input', (e) => {
          if (e.target.matches('input[name="quantity"]')) {
            syncAll(e.target.value, e.target);
          }
        });
      }

      bindVariantSelectors() {
        const selects = this.querySelectorAll('.sticky-atc-bar__variant-select');
        selects.forEach((select) => {
          select.addEventListener('change', (e) => {
            this.updateStickySwatch(e.target);
            this.syncOptionToMainForm(e.target.dataset.optionName, e.target.value);
          });
        });

        const radios = this.querySelectorAll('.sticky-atc-bar__variant-radio');
        radios.forEach((radio) => {
          radio.addEventListener('change', (e) => {
            if (e.target.checked) {
              this.syncOptionToMainForm(e.target.dataset.optionName, e.target.value);
              // Update mobile option value label if it exists
              const optionWrapper = e.target.closest('.sticky-atc-bar__mobile-option');
              if (optionWrapper) {
                const valLabel = optionWrapper.querySelector('.sticky-atc-bar__mobile-val');
                if (valLabel) valLabel.textContent = e.target.value;
              }
            }
          });
        });
      }



      onClick() {
        this.submitEls.forEach(btn => {
          btn.setAttribute('aria-disabled', 'true');
          btn.classList.add('btn--loading');
        });
      }

      resetButtonLoading() {
        this.submitEls.forEach(btn => {
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('btn--loading');
        });
      }

      scrollToTop(target, offset = 200) {
        const scrollIntoView = (selector, offset) => {
          window.scrollTo({
            behavior: 'smooth',
            top: selector.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
          });
        };

        scrollIntoView(target, offset);
      }

      updateImage(variant) {
        if (!variant || !variant.featured_image) return;
        const imgNodes = this.querySelectorAll('.sticky-atc-bar__product-image img');
        if (!imgNodes.length) return;
        const src = variant.featured_image.src || variant.featured_image.url;
        if (!src) return;

        imgNodes.forEach(imgNode => {
          imgNode.setAttribute('src', src);
          imgNode.setAttribute('srcset', src);
        });
      }

      updateButton(disable = true, text, modifyClass = true) {
        this.submitEls.forEach(addButton => {
          if (!addButton) return;

          const addButtonText = addButton.querySelector('span');
          if (disable) {
            addButton.setAttribute('disabled', 'disabled');
            if (text && addButtonText) addButtonText.textContent = text;
          } else {
            addButton.removeAttribute('disabled');
            if (addButtonText) addButtonText.textContent = FoxTheme.variantStrings.addToCart;
          }
        });
      }

      updatePrice(variant) {
        if (!variant) return;

        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { compare_at_price, price, unit_price_measurement } = variant;

        const priceWrappers = this.querySelectorAll('.f-price');
        priceWrappers.forEach(priceWrapper => {
          // On sale.
          if (compare_at_price && compare_at_price > price) {
            priceWrapper.classList.add(classes.onSale);
          } else {
            priceWrapper.classList.remove(classes.onSale);
          }

          // Sold out.
          if (!variant.available) {
            priceWrapper.classList.add(classes.soldOut);
          } else {
            priceWrapper.classList.remove(classes.soldOut);
          }

          const salePrice = priceWrapper.querySelector('.f-price-item--sale');
          if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

          const compareAtPrices = priceWrapper.querySelectorAll('.f-price-item--regular');
          if (compareAtPrices.length > 0) {
            if (compare_at_price && compare_at_price > price) {
              compareAtPrices.forEach(
                (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
              );
            } else {
              compareAtPrices.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
            }
          }

          const unitPriceWrapper = priceWrapper.querySelector('.f-price__unit-wrapper');
          if (unitPriceWrapper) {
            const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');
            if (unit_price_measurement && unitPrice) {
              unitPriceWrapper.classList.remove('hidden');
              const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
                variant.unit_price,
                moneyFormat
              )}</span>/<span data-unit-price-base-unit>${FoxTheme.Currency.getBaseUnit(variant)}</span>`;
              unitPrice.innerHTML = unitPriceContent;
            } else {
              unitPriceWrapper.classList.add('hidden');
            }
          }
        });
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          this.updatePrice(variant);
          this.updateImage(variant);
          this.updateButton(true, '', false);
          this.updateStickySelectors(variant);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          }
        });
      }

      updateStickySelectors(variant) {
        if (!variant) return;
        variant.options.forEach((optionValue, idx) => {
          // Update Custom Dropdowns (Desktop & Mobile)
          const customWrappers = this.querySelectorAll(`.sticky-atc-bar__option-wrap[data-option-index="${idx}"]`);
          customWrappers.forEach(customWrapper => {
            const input = customWrapper.querySelector('.sticky-atc-bar__custom-select-input');
            const items = customWrapper.querySelectorAll('.sticky-atc-bar__custom-select-item');
            const label = customWrapper.querySelector('.sticky-atc-bar__custom-select-label');
            const triggerSwatch = customWrapper.querySelector('.sticky-atc-bar__custom-select-trigger .swatch-color');

            if (input && input.value !== optionValue) {
              input.value = optionValue;
              if (label) label.textContent = optionValue;

              items.forEach(item => {
                if (item.dataset.value === optionValue) {
                  item.classList.add('is-selected');
                  const swatchBg = item.dataset.swatch;
                  if (triggerSwatch) {
                    if (swatchBg) {
                      triggerSwatch.style.cssText = `--swatch-background: ${swatchBg}; width: 20px; height: 20px; box-shadow: inset 0 0 0 1px rgba(0,0,0,0.1), 0 0 0 2px rgb(var(--color-background, 255, 255, 255)), 0 0 0 3px rgb(var(--color-foreground, 0, 0, 0));`;
                      triggerSwatch.classList.remove('hidden');
                    } else {
                      triggerSwatch.classList.add('hidden');
                    }
                  }
                } else {
                  item.classList.remove('is-selected');
                }
              });
            }

            // Also update mobile specific label if it exists in this wrapper
            const valLabel = customWrapper.querySelector('.sticky-atc-bar__mobile-val');
            if (valLabel) valLabel.textContent = optionValue;
          });

          // Update Mobile Radios / Dropdowns
          const selects = this.querySelectorAll(`.sticky-atc-bar__variant-select[data-option-index="${idx}"]`);
          selects.forEach(select => {
            if (select && select.value !== optionValue) {
              select.value = optionValue;
            }
          });

          const radios = this.querySelectorAll(`.sticky-atc-bar__variant-radio[data-option-index="${idx}"]`);
          radios.forEach(radio => {
            if (radio.value === optionValue) {
              radio.checked = true;
              const valLabel = radio.closest('.sticky-atc-bar__mobile-option')?.querySelector('.sticky-atc-bar__mobile-val');
              if (valLabel) valLabel.textContent = optionValue;
            }
          });
        });

        const summary = this.querySelector('.sticky-atc-bar__mobile-summary');
        if (summary) {
          summary.innerHTML = `${variant.title} - ${FoxTheme.Currency.formatMoney(variant.price, FoxTheme.settings.moneyFormat)}`;
        }
      }
    }
  );
}
